import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { getChoices, saveChoices } from "@/lib/choices";
import { Mic, Square, Loader2, X, Volume2, Keyboard } from "lucide-react";
import { toast } from "sonner";

function speak(text) {
  try {
    if (!text || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 1.02;
    window.speechSynthesis.speak(u);
  } catch { /* ignore */ }
}

export default function VoiceAssistant() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [state, setState] = useState("idle"); // idle | recording | thinking
  const [result, setResult] = useState(null); // {transcript, reply}
  const [typed, setTyped] = useState("");
  const [showType, setShowType] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const runAction = (action) => {
    if (!action) return;
    if (action.type === "navigate") nav(action.to);
    else if (action.type === "open_rodeo") nav(`/rodeo/${action.rodeo_id}${action.tab ? `?tab=${action.tab}` : ""}`);
    else if (action.type === "set_preference") {
      const all = getChoices();
      const cur = all[action.rodeo_id] || { pref1: null, pref2: null };
      cur[action.preference === 2 ? "pref2" : "pref1"] = action.performance_id;
      all[action.rodeo_id] = cur;
      saveChoices(all);
      toast.success(`${action.rodeo_name}: ${action.performance_label} set as ${action.preference === 2 ? "2nd" : "1st"} preference`);
    }
  };

  const handleResponse = (data) => {
    setResult({ transcript: data.transcript, reply: data.reply });
    speak(data.reply);
    runAction(data.action);
  };

  const sendAudio = async (blob) => {
    setState("thinking");
    try {
      const fd = new FormData();
      fd.append("audio", blob, "command.webm");
      const { data } = await api.post("/voice/command", fd);
      handleResponse(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't process that");
      setResult({ transcript: "", reply: "Sorry, I didn't catch that. Try again." });
    } finally { setState("idle"); }
  };

  const sendText = async () => {
    if (!typed.trim()) return;
    setState("thinking");
    try {
      const fd = new FormData();
      fd.append("text", typed.trim());
      const { data } = await api.post("/voice/command", fd);
      handleResponse(data);
      setTyped("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't process that");
    } finally { setState("idle"); }
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        sendAudio(new Blob(chunksRef.current, { type: "audio/webm" }));
      };
      mr.start();
      mediaRef.current = mr;
      setState("recording");
    } catch {
      toast.error("Mic access denied — you can type a command instead");
      setShowType(true);
    }
  };
  const stopRec = () => { if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop(); };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="voice-fab"
        className="fixed bottom-24 md:bottom-6 right-4 md:right-6 z-[1200] w-14 h-14 rounded-full bg-[#C8000F] hover:bg-[#9E0008] text-white shadow-lg shadow-black/20 flex items-center justify-center"
        title="Voice assistant" aria-label="Voice assistant">
        <Mic className="w-6 h-6" />
      </button>

      {open && (
        <div className="fixed inset-0 z-[1300] flex items-end md:items-center justify-center bg-black/40" onClick={() => { stopRec(); setOpen(false); }} data-testid="voice-panel">
          <div onClick={(e) => e.stopPropagation()} className="w-full md:max-w-md bg-[#12161C] text-slate-100 rounded-t-2xl md:rounded-2xl border border-white/10 p-5 pb-7">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 font-display font-bold"><Volume2 className="w-5 h-5 text-[#C8000F]" /> Voice assistant</div>
              <button onClick={() => { stopRec(); setOpen(false); }} data-testid="voice-close" className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            <div className="flex flex-col items-center py-4">
              <button
                onClick={state === "recording" ? stopRec : startRec}
                disabled={state === "thinking"}
                data-testid="voice-record-btn"
                className={`w-20 h-20 rounded-full flex items-center justify-center transition-all ${state === "recording" ? "bg-[#C8000F] animate-pulse" : state === "thinking" ? "bg-white/10" : "bg-[#C8000F] hover:bg-[#9E0008]"}`}>
                {state === "thinking" ? <Loader2 className="w-8 h-8 animate-spin" /> : state === "recording" ? <Square className="w-7 h-7 fill-white" /> : <Mic className="w-8 h-8" />}
              </button>
              <div className="text-xs text-slate-400 mt-3 h-4">
                {state === "recording" ? "Listening… tap to stop" : state === "thinking" ? "Thinking…" : "Tap and speak a command"}
              </div>
            </div>

            {result && (
              <div className="rounded-xl bg-white/5 border border-white/10 p-3 mb-3" data-testid="voice-result">
                {result.transcript ? <div className="text-[11px] text-slate-500 mb-1">You said: "{result.transcript}"</div> : null}
                <div className="text-sm text-slate-100">{result.reply}</div>
              </div>
            )}

            <button onClick={() => setShowType((s) => !s)} data-testid="voice-type-toggle" className="text-xs text-slate-400 flex items-center gap-1.5 mx-auto">
              <Keyboard className="w-3.5 h-3.5" /> {showType ? "Hide" : "Type a command instead"}
            </button>
            {showType && (
              <div className="flex gap-2 mt-2">
                <input value={typed} onChange={(e) => setTyped(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendText()}
                  placeholder={`e.g. "What's the count at Strathmore?"`} data-testid="voice-text-input"
                  className="flex-1 rounded-lg bg-white/5 border border-white/10 text-slate-100 px-3 py-2.5 text-sm placeholder:text-slate-500" />
                <button onClick={sendText} disabled={state === "thinking"} data-testid="voice-text-send" className="bg-[#C8000F] hover:bg-[#9E0008] text-white text-sm font-bold px-4 rounded-lg disabled:opacity-50">Send</button>
              </div>
            )}

            <div className="text-[11px] text-slate-500 mt-4 leading-relaxed">
              Try: "Set Strathmore to first preference" · "What's the count at Ponoka?" · "Open the Standings" · "Take me to the map"
            </div>
          </div>
        </div>
      )}
    </>
  );
}
